import { useState } from "react";
import { odemeData, OdemeDurumu, OdemeKayit } from "@/mocks/takip";

const DURUM_STYLE: Record<OdemeDurumu, string> = {
  "Yaklaşıyor": "bg-amber-100 text-amber-700",
  "Bugün": "bg-orange-100 text-orange-700",
  "Hatırlatma Gönderildi": "bg-sky-100 text-sky-700",
  "Gecikti": "bg-red-100 text-red-600",
  "Tamamlandı": "bg-emerald-100 text-emerald-700",
};
const DURUM_ICON: Record<OdemeDurumu, string> = {
  "Yaklaşıyor": "ri-time-line",
  "Bugün": "ri-calendar-event-line",
  "Hatırlatma Gönderildi": "ri-send-plane-line",
  "Gecikti": "ri-error-warning-line",
  "Tamamlandı": "ri-checkbox-circle-line",
};

const FILTER_TABS: { key: string; label: string; durum?: OdemeDurumu[] }[] = [
  { key: "tumu", label: "Tümü" },
  { key: "bugun", label: "Bugün", durum: ["Bugün"] },
  { key: "yaklasan", label: "1–3 Gün", durum: ["Yaklaşıyor"] },
  { key: "hatirlandi", label: "Hatırlatma Gönderildi", durum: ["Hatırlatma Gönderildi"] },
  { key: "gecikti", label: "Geciken", durum: ["Gecikti"] },
  { key: "tamamlandi", label: "Tamamlandı", durum: ["Tamamlandı"] },
];

interface HatirlatModal { kayit: OdemeKayit; }

export default function OdemePage() {
  const [activeFilter, setActiveFilter] = useState("tumu");
  const [search, setSearch] = useState("");
  const [hatirlatModal, setHatirlatModal] = useState<HatirlatModal | null>(null);
  const [confirmSent, setConfirmSent] = useState(false);

  const bugun = odemeData.filter(o => o.durum === "Bugün").length;
  const yarin = odemeData.filter(o => o.kalanGun === 1).length;
  const ucGun = odemeData.filter(o => o.kalanGun >= 2 && o.kalanGun <= 3).length;
  const hatirlandi = odemeData.filter(o => o.durum === "Hatırlatma Gönderildi").length;
  const gecikti = odemeData.filter(o => o.durum === "Gecikti").length;

  const filtered = odemeData.filter(o => {
    const s = search.toLowerCase();
    const matchSearch = !s || o.veliAdi.toLowerCase().includes(s) || o.ogrenciAdi.toLowerCase().includes(s);
    const tab = FILTER_TABS.find(t => t.key === activeFilter);
    const matchFilter = !tab?.durum || tab.durum.includes(o.durum);
    return matchSearch && matchFilter;
  });

  const handleSendConfirm = () => {
    setConfirmSent(true);
    setTimeout(() => { setConfirmSent(false); setHatirlatModal(null); }, 1800);
  };

  return (
    <div className="p-4 md:p-6 xl:p-8 space-y-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Ödeme Takibi</h2>
          <p className="text-sm text-gray-500 mt-0.5">Yaklaşan ödeme günlerini takip edin ve hatırlatma gönderin</p>
        </div>
        <button className="px-5 py-2.5 text-sm bg-[#25D366] text-white rounded-xl hover:bg-[#20bd5a] cursor-pointer whitespace-nowrap flex items-center gap-2 font-semibold transition-colors">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line text-sm"></i></div>
          Toplu Hatırlatma Gönder
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Bugün Ödeme Günü", value: bugun, icon: "ri-calendar-event-line", color: "bg-orange-500 text-white" },
          { label: "Yarın Ödeme Günü", value: yarin, icon: "ri-calendar-2-line", color: "bg-amber-500 text-white" },
          { label: "3 Gün İçinde", value: ucGun, icon: "ri-time-line", color: "bg-sky-500 text-white" },
          { label: "Hatırlatma Gönderildi", value: hatirlandi, icon: "ri-send-plane-line", color: "bg-[#25D366] text-white" },
          { label: "Geciken Ödeme", value: gecikti, icon: "ri-error-warning-line", color: "bg-red-500 text-white" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-[11px] text-gray-500 leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Info card */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600">
          <i className="ri-information-line text-sm"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-800 mb-0.5">GoStudent Ödeme Hatırlatması</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            GoStudent ödemeleri için velilere ödeme gününden önce kartlarının <strong>yurt dışı online ödeme</strong> kullanımını açık bırakmaları hatırlatılabilir. Bu bilgi özellikle ilk ödeme yapacak veliler için kritik öneme sahiptir.
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row gap-3">
          <div className="flex flex-wrap gap-2 flex-1">
            {FILTER_TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveFilter(t.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors
                  ${activeFilter === t.key ? "bg-[#0B1120] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-64">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Veli / öğrenci ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366]"
            />
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {["Veli Adı", "Öğrenci Adı", "Telefon", "Ödeme Günü", "Kalan Gün", "Son Hatırlatma", "Durum", "İşlem"].map(h => (
                  <th key={h} className="text-left text-[11px] font-semibold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 text-xs font-bold flex-shrink-0">
                        {o.veliAdi.charAt(0)}
                      </div>
                      <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{o.veliAdi}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{o.ogrenciAdi}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{o.telefon}</td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-bold text-gray-800">Her ayın {o.odemeGunu}. günü</span>
                  </td>
                  <td className="px-4 py-3">
                    {o.kalanGun < 0
                      ? <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-lg">{Math.abs(o.kalanGun)} gün geçti</span>
                      : o.kalanGun === 0
                      ? <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-lg">Bugün</span>
                      : <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded-lg">{o.kalanGun} gün</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {o.sonHatirlatma === "-" ? <span className="text-gray-300">Henüz yok</span> : o.sonHatirlatma}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${DURUM_STYLE[o.durum]}`}>
                      <i className={`${DURUM_ICON[o.durum]} text-xs`}></i>
                      {o.durum}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setHatirlatModal({ kayit: o })}
                        title="Hatırlatma Gönder"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 cursor-pointer transition-colors"
                      >
                        <i className="ri-whatsapp-line text-xs"></i>
                      </button>
                      <button
                        title="Tamamlandı İşaretle"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 cursor-pointer transition-colors"
                      >
                        <i className="ri-checkbox-circle-line text-xs"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <i className="ri-search-line text-gray-400 text-xl"></i>
              </div>
              <p className="text-sm text-gray-400">Kayıt bulunamadı</p>
            </div>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map(o => (
            <div key={o.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 text-sm font-bold flex-shrink-0">
                    {o.veliAdi.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{o.veliAdi}</p>
                    <p className="text-xs text-gray-500">{o.ogrenciAdi}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${DURUM_STYLE[o.durum]}`}>
                  {o.durum}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Her ayın {o.odemeGunu}. günü</span>
                <span className="font-mono">{o.telefon}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setHatirlatModal({ kayit: o })}
                  className="flex-1 py-2 text-xs font-semibold bg-[#25D366]/10 text-[#25D366] rounded-xl cursor-pointer flex items-center justify-center gap-1"
                >
                  <i className="ri-whatsapp-line"></i> Hatırlatma Gönder
                </button>
                <button className="flex-1 py-2 text-xs font-semibold bg-emerald-50 text-emerald-600 rounded-xl cursor-pointer flex items-center justify-center gap-1">
                  <i className="ri-checkbox-circle-line"></i> Tamamlandı
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">{filtered.length} kayıt gösteriliyor</p>
        </div>
      </div>

      {/* Confirm Modal */}
      {hatirlatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !confirmSent && setHatirlatModal(null)} />
          <div className="relative bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 space-y-5">
            {confirmSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-checkbox-circle-line text-emerald-600 text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-800">Hatırlatma gönderildi!</p>
                <p className="text-xs text-gray-500 mt-1">{hatirlatModal.kayit.veliAdi} kişisine mesaj eklendi</p>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-whatsapp-line text-[#25D366] text-2xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900">Hatırlatma Mesajı Gönder</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    <strong>{hatirlatModal.kayit.veliAdi}</strong> kişisine ödeme hatırlatma mesajı gönderilecek. Onaylıyor musunuz?
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setHatirlatModal(null)}
                    className="flex-1 py-2.5 text-sm border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    Vazgeç
                  </button>
                  <button
                    onClick={handleSendConfirm}
                    className="flex-1 py-2.5 text-sm bg-[#25D366] text-white rounded-xl hover:bg-[#20bd5a] cursor-pointer font-semibold transition-colors"
                  >
                    Evet, Gönder
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}